import { translateManager as t } from '../../services/TranslateManager'
import { giftNoteManager } from '../../services/GiftNoteManager'
import { promoCodeManager } from '../../services/PromoCodeManager'
import { PROMO_CODE_SOURCE } from '../../stores/promoCodeStore/promoCode.consts'
import { saveOrderDraftApi } from '../../api/orderDraft.api'
import { SNACKBAR_VARIANTS } from '../../elements/Snackbar/Snackbar.consts'
import { getTilesStore } from '../../stores/RootStoreProvider'
import { logger } from '../../services/logger'
import { analytics } from '../../services/Analytics/Analytics'
import { isCenterpiece } from '@mixtiles/web-backend-shared'

export default async function syncOrder(api, currentAccount, enqueueSnackbar) {
  const giftNote = giftNoteManager.getGiftNote()
  const discountCoupon = promoCodeManager.getDiscountCouponDisplayName()
  const { orderDraft } = currentAccount

  // Unconditional (even with no tiles) so the sync-ready flag flips on every login —
  // otherwise a tiles-less account would never sync newly created tiles.
  const restoredTiles = getTilesStore().onAccountSyncReady(
    patchOrderDraftTiles(orderDraft?.tiles)
  )
  notifyRestoredTiles(restoredTiles, enqueueSnackbar, 'Account Sync')

  const hasRemoteGiftNote = orderDraft?.giftNote
  const hasRemoteDiscountCoupon = !!orderDraft?.discountCoupon
  let shouldSave =
    (giftNote && !hasRemoteGiftNote) ||
    (discountCoupon && !hasRemoteDiscountCoupon)

  if (orderDraft) {
    if (orderDraft.giftNote && !giftNote) {
      giftNoteManager.setGiftNote(orderDraft.giftNote)
    } else if (
      orderDraft.giftNote &&
      giftNote &&
      orderDraft.giftNote !== giftNote
    ) {
      shouldSave = true
    }

    if (hasRemoteDiscountCoupon && !discountCoupon) {
      await promoCodeManager.addPromoCode({
        code: orderDraft.discountCoupon,
        source: PROMO_CODE_SOURCE.ACCOUNT,
        persist: false,
      })
    } else if (
      hasRemoteDiscountCoupon &&
      discountCoupon &&
      discountCoupon !== orderDraft.discountCoupon
    ) {
      shouldSave = true
    }
  }

  if (shouldSave) {
    await api
      .call(
        saveOrderDraftApi({
          giftNote: giftNoteManager.getGiftNote(),
          discountCoupon: promoCodeManager.getDiscountCouponCode(),
        })
      )
      .catch((e) => logger.error('Save order draft failed during sync', e))
  }
}

// A centerpiece designed in the app stores its image as `originalUrl`, but one
// that came from a gallery wall stores it as `url` — and a wall checkout saves
// its tiles into the draft verbatim. The tile model only reads `originalUrl`,
// so a wall centerpiece restored from a draft would arrive with no image at all.
const withCenterpieceImageUrl = (tile) =>
  isCenterpiece(tile) && !tile.originalUrl && tile.url
    ? { ...tile, originalUrl: tile.url }
    : tile

export const patchOrderDraftTiles = (orderDraftTiles) =>
  (orderDraftTiles ?? []).map(withCenterpieceImageUrl).map((t) => ({
    ...t,
    cropParams: t.cropParams || {},
    remote: true,
    // filterImageUrl might point to a non existing local blob url, so we need to remove it
    filterImageUrl: undefined,
  }))

const notifyRestoredTiles = (restoredTiles, enqueueSnackbar, source) => {
  if (!restoredTiles.length) return

  const restoredText =
    restoredTiles.length === 1
      ? t.get('login.restored_photo')
      : t.get('login.restored_photos')
  setTimeout(
    () =>
      enqueueSnackbar('', {
        variant: SNACKBAR_VARIANTS.COMMON,
        title: restoredText,
        name: 'Sync Order',
      }),
    2000
  )

  analytics.track('Restored photos', {
    photosNum: restoredTiles.length,
    Source: source,
  })
}

// Restores tiles without enabling sync — for non-login flows (e.g. PhotoStyler re-order).
export const restoreTilesAndNotify = (
  orderDraftTiles,
  enqueueSnackbar,
  source
) => {
  const restoredTiles = getTilesStore().restoreRawTiles(
    patchOrderDraftTiles(orderDraftTiles)
  )
  notifyRestoredTiles(restoredTiles, enqueueSnackbar, source)
  return restoredTiles
}
